import {Outlet} from 'react-router-dom'
import Header from '../header/Header'
import {useNavigate, useLocation} from "react-router-dom";
import '@nike/eds/dist/index.css'
import './AuthorizedLayout.css'
import {useOktaAuth} from '@okta/okta-react'
import {AuthState, toRelativeUrl} from '@okta/okta-auth-js'
import LoginError from '../../pages/LoginError'
import {CCHNavigation, useApplications} from '@nike/cch-shared-ui-lib';
import {useState, useEffect} from 'react';
import {NavEvent, NavItem} from "@nike/eds/dist/components/VerticalNavbar/VerticalNavbar";
import {setActiveValueInNavItems} from "@nike/eds";
import {NikeSwoosh} from "@nike/nike-design-system-icons";

function AuthorizedLayout({requiredGroup}) {
    const {oktaAuth, authState} = useOktaAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const isAuthenticated = authState?.isAuthenticated;
    const isAuthorized = hasAuthorizedGroup(authState, requiredGroup);

    if (!isAuthenticated) {
        const originalUri = toRelativeUrl(window.location.href, window.location.origin);
        oktaAuth.setOriginalUri(originalUri);
        oktaAuth.signInWithRedirect();
    }

    if (!isAuthorized) {
        return (<LoginError requiredGroup={requiredGroup}/>);
    }

    const [navItems, setNavItems] = useState<NavItem[]>([
        {
            id: "1",
            icon: "List",
            label: "Batches",
            active: true,
        },
        {
            id: "2",
            icon: "InfoCircle",
            label: "Exceptions",
            active: false,
        },
        {
            id: "3",
            icon: "Calendar",
            label: "Declaration Periods",
            active: false,
        },
        {
            id: "4",
            icon: "Wallet",
            label: "Filing Packs",
            active: false,
        },
        {
            id: "5",
            icon: "Shoe",
            label: "Product Weights",
            active: false,
        }
    ]);

    const NAV_PATHS: Record<string, string> = {
        "1": "/local-clearance/batches",
        "2": "/local-clearance/exceptions",
        "3": "/local-clearance/declaration-periods",
        "4": "/local-clearance/filing-packs",
        "5": "/product-weights",
    };

    // Sync active nav item with current URL
    useEffect(() => {
        const pathToId = Object.entries(NAV_PATHS).reduce((acc, [id, path]) => {
            acc[path] = id;
            return acc;
        }, {} as Record<string, string>);

        const currentPath = location.pathname;
        const activeId = pathToId[currentPath];

        if (activeId) {
            const newItems = setActiveValueInNavItems(activeId, navItems) as NavItem[];
            setNavItems(newItems);
        }
    }, [location.pathname]);

    const handleNavigate = (item: NavEvent) => {
        const newApplicationItems = setActiveValueInNavItems(item.id, navItems) as NavItem[];
        setNavItems(newApplicationItems);
        const path = NAV_PATHS[item.id] || "/";
        navigate(path);
        // Dispatch popstate event so microfrontend's BrowserRouter detects the URL change
        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    const {applications, loading} = useApplications({
        applicationsEndpoint: process.env.REACT_APP_APPLICATION_RETRIEVER,
        getAccessToken: () => oktaAuth.getAccessToken(),
    });

    return (
        <>
            <div className="container">
                <CCHNavigation
                    appName=""
                    items={navItems}
                    onNavigate={handleNavigate}
                    applications={loading ? [] : (applications || [])}
                    homePath={"/local-clearance/batches"}
                    placement={"bottom"}

                />
                <div className="main">
                    <div className="main-header">
                        <Header/>
                    </div>
                    <div className="main-content">
                        <Outlet/>
                    </div>
                    <div className="page-footer">
                        <NikeSwoosh size="l" />
                    </div>
                </div>
            </div>
        </>
    )
}

function hasAuthorizedGroup(authState: AuthState | null, requiredGroup: string) {
    const groups = authState?.idToken?.claims?.groups;

    if (Array.isArray(groups)) {
        return groups.includes(requiredGroup);
    }

    return false;
}

export default AuthorizedLayout
